// ──────────────────────────────────────────────────────────────────────────
// CERCARE NELLA GUIDA
//
// La ricerca scorre gli stessi testi che la guida mostra: niente domande
// scritte a parte, che resterebbero indietro alla prima modifica. Si cerca
// come si scrive sul telefono a bordo campo — senza maiuscole, senza
// accenti, a metà parola ("recup") — e ogni argomento trovato porta con sé
// i pezzi di frase dove compare, con le parole cercate da evidenziare.
//
// Con più parole un argomento le deve contenere tutte, anche in frasi
// diverse: "annulla corso" trova il corso che salta anche se le due parole
// non stanno vicine.
// ──────────────────────────────────────────────────────────────────────────

// I pezzi di frase che un argomento mostra, al massimo, e quanto testo
// tengono intorno alla prima parola trovata
const MAX_SNIPPETS = 2
const SNIPPET_BEFORE = 40
const SNIPPET_LENGTH = 140

// Le parole più corte di così trovano tutto e non dicono niente
export const MIN_TERM_LENGTH = 2

const stripMarks = text => text.replace(/\*\*/g, "")

// Il testo come si confronta — minuscolo, senza accenti — e, per ogni suo
// carattere, la posizione da cui viene nel testo vero: è lì che vanno
// messe le evidenziature.
const normalizeWithMap = text => {
	let normalized = ""
	const map = []
	for (let i = 0; i < text.length; i++) {
		const plain = text[i].normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
		for (let j = 0; j < plain.length; j++) {
			normalized += plain[j]
			map.push(i)
		}
	}
	return { normalized, map }
}

export const normalizeSearch = text => normalizeWithMap(text).normalized

export const searchTerms = query =>
	normalizeSearch(query ?? "")
		.split(/[^a-z0-9]+/)
		.filter(term => term.length >= MIN_TERM_LENGTH)

// Tutti i testi di un argomento, ognuno com'è letto: senza i segni del
// grassetto. Il titolo e la riga sotto contano come gli altri.
export const topicTexts = topic =>
	[
		topic.title,
		topic.hint,
		...topic.blocks.flatMap(block => {
			if (block.p) return [block.p]
			if (block.h) return [block.h]
			if (block.steps) return block.steps
			if (block.list) return block.list
			if (block.choices) return block.choices.map(choice => `${choice.title}: ${choice.text}`)
			return []
		}),
	].map(text => stripMarks(text ?? ""))

// Dove compaiono le parole in un testo, come intervalli [inizio, fine) sul
// testo vero, in ordine e senza sovrapposizioni
const findRanges = (text, terms) => {
	const { normalized, map } = normalizeWithMap(text)
	const ranges = []
	terms.forEach(term => {
		let from = normalized.indexOf(term)
		while (from !== -1) {
			const start = map[from]
			const end = map[from + term.length - 1] + 1
			ranges.push([start, end])
			from = normalized.indexOf(term, from + term.length)
		}
	})
	ranges.sort((a, b) => a[0] - b[0])
	return ranges.reduce((merged, range) => {
		const last = merged[merged.length - 1]
		if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
		else merged.push([...range])
		return merged
	}, [])
}

// Un pezzo di frase intorno alla prima parola trovata, tagliato su uno
// spazio, a pezzi: `{ text, hit }`, dove `hit` è una parola cercata
const snippetOf = (text, ranges) => {
	let start = Math.max(0, ranges[0][0] - SNIPPET_BEFORE)
	if (start > 0) {
		const space = text.indexOf(" ", start)
		start = space !== -1 && space < ranges[0][0] ? space + 1 : start
	}
	let end = Math.min(text.length, start + SNIPPET_LENGTH)
	if (end < text.length) {
		const space = text.lastIndexOf(" ", end)
		end = space > ranges[0][1] ? space : end
	}

	const parts = []
	if (start > 0) parts.push({ text: "…", hit: false })
	let cursor = start
	ranges
		.filter(([from, to]) => to > start && from < end)
		.forEach(([from, to]) => {
			const hitStart = Math.max(from, start)
			const hitEnd = Math.min(to, end)
			if (hitStart > cursor) parts.push({ text: text.slice(cursor, hitStart), hit: false })
			parts.push({ text: text.slice(hitStart, hitEnd), hit: true })
			cursor = hitEnd
		})
	if (cursor < end) parts.push({ text: text.slice(cursor, end), hit: false })
	if (end < text.length) parts.push({ text: "…", hit: false })
	return parts
}

// Gli argomenti che contengono tutte le parole, prima quelli che le hanno
// nel titolo, poi quelli dove compaiono più volte. Ognuno con i suoi pezzi
// di frase: i testi dove ci sono più parole diverse, nell'ordine della
// guida a parità.
export const searchGuide = (query, topics) => {
	const terms = searchTerms(query)
	if (terms.length === 0) return []

	return topics
		.map((topic, order) => {
			const texts = topicTexts(topic)
			const whole = normalizeSearch(texts.join("\n"))
			if (!terms.every(term => whole.includes(term))) return null

			const found = texts
				.map((text, index) => {
					const normalized = normalizeSearch(text)
					const termsHere = terms.filter(term => normalized.includes(term)).length
					return { text, index, termsHere, ranges: findRanges(text, terms) }
				})
				.filter(entry => entry.termsHere > 0)
			const inTitle = terms.some(term => normalizeSearch(topic.title).includes(term))
			const hits = found.reduce((sum, entry) => sum + entry.ranges.length, 0)
			// il titolo l'argomento lo mostra già: un pezzo di frase va preso
			// dal testo, finché il testo ne ha
			const bodyFound = found.filter(entry => entry.index > 0)
			const snippets = (bodyFound.length > 0 ? bodyFound : found)
				.sort((a, b) => b.termsHere - a.termsHere || a.index - b.index)
				.slice(0, MAX_SNIPPETS)
				.sort((a, b) => a.index - b.index)
				.map(entry => snippetOf(entry.text, entry.ranges))

			return { topic, snippets, inTitle, hits, order }
		})
		.filter(Boolean)
		.sort((a, b) => b.inTitle - a.inTitle || b.hits - a.hits || a.order - b.order)
		.map(({ topic, snippets }) => ({ topic, snippets }))
}

// Un testo della guida a pezzi, per il grassetto: `{ text, bold }`
export const inlineParts = text =>
	(text ?? "")
		.split(/(\*\*[^*]+\*\*)/)
		.filter(Boolean)
		.map(part =>
			part.startsWith("**") && part.endsWith("**")
				? { text: part.slice(2, -2), bold: true }
				: { text: part, bold: false },
		)
